import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { ChevronRight, XCircle, AlertTriangle, TrendingUp, Gift, CheckCircle } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn, formatCents } from '@/lib/utils';
import { useRoomCardData } from '@/hooks/useSupplementHelpers';
import { CategorySection } from '../cards/CategorySection';
import type { DisplayRoom } from '@/types/supplement';

interface SupplementRoomCardProps {
  room: DisplayRoom;
  supplementId: number;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Category section states - when true, all category sections should be open
  categorySectionsOpen?: boolean;
}

export function SupplementRoomCard({
  room,
  supplementId,
  isOpen: controlledIsOpen,
  onOpenChange,
  categorySectionsOpen: controlledCategorySectionsOpen,
}: SupplementRoomCardProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false); // Start collapsed
  const [unmatchedContractorOpen, setUnmatchedContractorOpen] = useState(false); // Start collapsed
  const [underscopedOpen, setUnderscopedOpen] = useState(false); // Start collapsed
  const [overscopedOpen, setOverscopedOpen] = useState(false);
  const [unmatchedInsurerOpen, setUnmatchedInsurerOpen] = useState(false);
  const [perfectMatchOpen, setPerfectMatchOpen] = useState(false);

  // Extract room data logic to hook
  const {
    unmatchedContractorItems,
    underscopedItems,
    overscopedItems,
    unmatchedInsurerItems,
    perfectMatchItems,
    displayRoomName,
    totalItems,
  } = useRoomCardData(room);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const handleOpenChange = (open: boolean) => {
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  // Sync category sections with controlled state
  useEffect(() => {
    if (controlledCategorySectionsOpen !== undefined) {
      setUnmatchedContractorOpen(controlledCategorySectionsOpen);
      setUnderscopedOpen(controlledCategorySectionsOpen);
      setOverscopedOpen(controlledCategorySectionsOpen);
      setUnmatchedInsurerOpen(controlledCategorySectionsOpen);
      setPerfectMatchOpen(controlledCategorySectionsOpen);
    }
  }, [controlledCategorySectionsOpen]);

  if (totalItems === 0) {
    return null;
  }

  return (
    <Card className="border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mb-4">
      <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
        {/* Room Header - Entire header is clickable */}
        <CollapsibleTrigger asChild>
          <div className="grid grid-cols-1 md:grid-cols-[minmax(300px,1fr)_auto_auto] items-center p-4 bg-slate-50/50 dark:bg-slate-900/50 border-b gap-4 cursor-pointer hover:bg-slate-100/50 dark:hover:bg-slate-800/50 transition-colors">

            {/* Left side: Chevron + Room Name */}
            <div className="flex items-start gap-3 min-w-0">
              <div className="h-8 w-8 p-0 text-muted-foreground flex items-center justify-center flex-shrink-0 mt-0.5 md:mt-0">
                <ChevronRight
                  className={cn(
                    'h-5 w-5 transition-transform duration-200',
                    isOpen && 'rotate-90'
                  )}
                />
              </div>

              <div className="min-w-0">
                <span className="font-semibold text-sm break-words block leading-tight md:leading-normal pt-1.5 md:pt-0">
                  {displayRoomName}
                </span>
              </div>
            </div>

            {/* Right side wrapper: Flex on mobile, flattened contents on desktop */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-end w-full md:w-auto pl-11 md:pl-0 min-w-0 md:contents">

              {/* Category Bubbles Container */}
              <div className="flex flex-wrap gap-2 justify-start sm:justify-end flex-1 md:flex-none min-w-0 md:justify-self-end">
                {/* Category Bubbles - Right aligned */}
                {unmatchedContractorItems.length > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-950 text-xs whitespace-nowrap">
                    <span className="text-red-700 dark:text-red-300 font-medium">Not on Insurance</span>
                    <span className="text-red-800 dark:text-red-200 font-semibold text-[10px]">
                      {unmatchedContractorItems.length}
                    </span>
                  </div>
                )}
                {underscopedItems.length > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-950 text-xs whitespace-nowrap">
                    <span className="text-orange-700 dark:text-orange-300 font-medium">Price Discrepancy</span>
                    <span className="text-orange-800 dark:text-orange-200 font-semibold text-[10px]">
                      {underscopedItems.length}
                    </span>
                  </div>
                )}
                {unmatchedInsurerItems.length > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950 text-xs whitespace-nowrap">
                    <span className="text-blue-700 dark:text-blue-300 font-medium">Free Scoped</span>
                    <span className="text-blue-800 dark:text-blue-200 font-semibold text-[10px]">
                      {unmatchedInsurerItems.length}
                    </span>
                  </div>
                )}
                {overscopedItems.length > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-950 text-xs whitespace-nowrap">
                    <span className="text-purple-700 dark:text-purple-300 font-medium">Overscoped</span>
                    <span className="text-purple-800 dark:text-purple-200 font-semibold text-[10px]">
                      {overscopedItems.length}
                    </span>
                  </div>
                )}
                {perfectMatchItems.length > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-950 text-xs whitespace-nowrap">
                    <span className="text-green-700 dark:text-green-300 font-medium">Perfect Match</span>
                    <span className="text-green-800 dark:text-green-200 font-semibold text-[10px]">
                      {perfectMatchItems.length}
                    </span>
                  </div>
                )}
              </div>

              {/* Amount - Right aligned */}
              {room.supplementAmountCents > 0 && (
                <div className="text-right flex-shrink-0 min-w-[80px] md:min-w-0">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    Supplement Amount
                  </div>
                  <div className="text-sm font-bold font-mono text-green-600 dark:text-green-400">
                    {formatCents(room.supplementAmountCents || 0)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsibleTrigger>

        {/* Room Content */}
        <CollapsibleContent>
          <div className="p-4 space-y-3">
            {/* Items Not on Insurance (Unmatched Contractor) */}
            <CategorySection
              title="Items Not on Insurance"
              items={unmatchedContractorItems}
              supplementId={supplementId}
              isOpen={unmatchedContractorOpen}
              onOpenChange={setUnmatchedContractorOpen}
              colorClass="red"
              icon={XCircle}
            />

            {/* Price Discrepancies (Underscoped) */}
            <CategorySection
              title="Price Discrepancies"
              items={underscopedItems}
              supplementId={supplementId}
              isOpen={underscopedOpen}
              onOpenChange={setUnderscopedOpen}
              colorClass="orange"
              icon={AlertTriangle}
            />

            {/* Overscoped */}
            <CategorySection
              title="Overscoped"
              items={overscopedItems}
              supplementId={supplementId}
              isOpen={overscopedOpen}
              onOpenChange={setOverscopedOpen}
              colorClass="purple"
              icon={TrendingUp}
            />

            {/* Free Scoped (Unmatched Insurer) */}
            <CategorySection
              title="Free Scoped"
              items={unmatchedInsurerItems}
              supplementId={supplementId}
              isOpen={unmatchedInsurerOpen}
              onOpenChange={setUnmatchedInsurerOpen}
              colorClass="blue"
              icon={Gift}
            />

            {/* Perfect Matches */}
            <CategorySection
              title="Perfect Matches"
              items={perfectMatchItems}
              supplementId={supplementId}
              isOpen={perfectMatchOpen}
              onOpenChange={setPerfectMatchOpen}
              colorClass="green"
              icon={CheckCircle}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
